import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { HandHeart, MessagesSquare } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { T } from "@/constants/testIds";

export default function AskPray() {
  const [askOpen, setAskOpen] = useState(false);
  const [prayOpen, setPrayOpen] = useState(false);

  const [askForm, setAskForm] = useState({ question: "", email: "" });
  const [prayForm, setPrayForm] = useState({ name: "", request: "", urgent: false, consent: false });

  const submitAsk = async (e) => {
    e.preventDefault();
    try {
      await api.post("/forms/ask", askForm);
      toast.success("Your question has been received. An elder will respond within a few days.");
      setAskForm({ question: "", email: "" });
      setAskOpen(false);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    }
  };

  const submitPrayer = async (e) => {
    e.preventDefault();
    if (!prayForm.consent) return toast.error("Please confirm privacy consent.");
    try {
      await api.post("/forms/prayer", prayForm);
      toast.success("Your prayer request is with our team. It will never be shared publicly.");
      setPrayForm({ name: "", request: "", urgent: false, consent: false });
      setPrayOpen(false);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    }
  };

  return (
    <section data-testid={T.askPray} className="py-24 sm:py-32">
      <div className="container-editorial grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="paper-card p-10">
          <MessagesSquare className="h-6 w-6 text-forest" />
          <h3 className="heading-3 mt-4">Ask a Bible question</h3>
          <p className="body-copy mt-3 text-ink-muted">
            Send us your honest question about God, the Bible, or the Christian life. A reviewer will respond
            personally, or point you to a careful, Scripture-rooted answer.
          </p>
          {!askOpen ? (
            <button data-testid={T.askQuestionBtn} onClick={() => setAskOpen(true)} className="btn-primary mt-6">
              Ask a Question
            </button>
          ) : (
            <form data-testid={T.askForm} onSubmit={submitAsk} className="mt-6 space-y-4">
              <input
                data-testid={T.askEmail}
                type="email"
                placeholder="Your email (optional)"
                value={askForm.email}
                onChange={(e) => setAskForm({ ...askForm, email: e.target.value })}
                className="w-full border border-border bg-paper px-4 py-3 text-sm focus:outline-none focus:border-ink"
              />
              <textarea
                data-testid={T.askQuestion}
                required
                rows={4}
                placeholder="What is your question?"
                value={askForm.question}
                onChange={(e) => setAskForm({ ...askForm, question: e.target.value })}
                className="w-full border border-border bg-paper px-4 py-3 text-sm focus:outline-none focus:border-ink"
              />
              <div className="flex items-center gap-3">
                <button data-testid={T.askSubmit} className="btn-primary" type="submit">
                  Send Question
                </button>
                <button type="button" className="text-sm text-ink-muted underline" onClick={() => setAskOpen(false)}>
                  Cancel
                </button>
              </div>
            </form>
          )}
        </div>

        <div className="paper-card p-10 bg-subtle">
          <HandHeart className="h-6 w-6 text-terracotta" />
          <h3 className="heading-3 mt-4">Request prayer</h3>
          <p className="body-copy mt-3 text-ink-muted">
            Prayer requests are never displayed publicly. Our elders will pray for you personally. This site is not a
            substitute for medical, legal, or emergency counselling — please call local services in a crisis.
          </p>
          {!prayOpen ? (
            <button data-testid={T.requestPrayerBtn} onClick={() => setPrayOpen(true)} className="btn-ghost mt-6">
              Request Prayer
            </button>
          ) : (
            <form data-testid={T.prayerForm} onSubmit={submitPrayer} className="mt-6 space-y-4">
              <input
                data-testid={T.prayerName}
                type="text"
                placeholder="Your name (optional)"
                value={prayForm.name}
                onChange={(e) => setPrayForm({ ...prayForm, name: e.target.value })}
                className="w-full border border-border bg-paper px-4 py-3 text-sm focus:outline-none focus:border-ink"
              />
              <textarea
                data-testid={T.prayerText}
                required
                rows={4}
                placeholder="How can we pray for you?"
                value={prayForm.request}
                onChange={(e) => setPrayForm({ ...prayForm, request: e.target.value })}
                className="w-full border border-border bg-paper px-4 py-3 text-sm focus:outline-none focus:border-ink"
              />
              <label className="flex items-start gap-2 text-xs text-ink-muted">
                <input
                  data-testid={T.prayerUrgent}
                  type="checkbox"
                  checked={prayForm.urgent}
                  onChange={(e) => setPrayForm({ ...prayForm, urgent: e.target.checked })}
                  className="mt-1"
                />
                <span>This is urgent</span>
              </label>
              <label className="flex items-start gap-2 text-xs text-ink-muted">
                <input
                  data-testid={T.prayerConsent}
                  type="checkbox"
                  checked={prayForm.consent}
                  onChange={(e) => setPrayForm({ ...prayForm, consent: e.target.checked })}
                  className="mt-1"
                />
                <span>I understand my request will remain private and not be shared publicly.</span>
              </label>
              <div className="flex items-center gap-3">
                <button data-testid={T.prayerSubmit} className="btn-primary" type="submit">
                  Send Prayer Request
                </button>
                <button type="button" className="text-sm text-ink-muted underline" onClick={() => setPrayOpen(false)}>
                  Cancel
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </section>
  );
}
